import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  Description as DocumentIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';

const AddEmployee = ({
  isOpen,
  onClose,
  onAddEmployee,
  businessOptions,
  branchOptions,
  departmentOptions,
  designationOptions,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);

  const [newEmployee, setNewEmployee] = useState({
    // Personal Details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    emergencyContact: '',
    emergencyPhone: '',
    password: '',
    role: '',

    // Company Details
    employeeCode: '',
    businessId: '',
    branchId: '',
    department: '',
    designation: '',
    reportingManager: '',
    dateOfJoining: '',
    employmentType: '',
    workLocation: '',
    shift: '',

    // Bank Details
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    upiId: '',
  });

  const resetForm = () => {
    setNewEmployee({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: '',
      address: '',
      emergencyContact: '',
      emergencyPhone: '',
      password: '',
      role: '',
      employeeCode: '',
      businessId: '',
      branchId: '',
      department: '',
      designation: '',
      reportingManager: '',
      dateOfJoining: '',
      employmentType: '',
      workLocation: '',
      shift: '',
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      accountHolderName: '',
      upiId: '',
    });
    setProfilePhoto(null);
    setProfilePhotoPreview(null);
    setActiveTab(0);
  };

  const handleSubmit = () => {
    const employeeData = {
      name: `${newEmployee.firstName} ${newEmployee.lastName}`,
      code: newEmployee.employeeCode,
      email: newEmployee.email,
      businessId: Number(newEmployee.businessId),
      branchId: Number(newEmployee.branchId),
      status: 'active',
      profilePhoto: profilePhoto,
      ...newEmployee,
    };
    onAddEmployee(employeeData);
    resetForm();
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const filteredBranches = newEmployee.businessId
    ? branchOptions.filter((b) => b.businessId.toString() === newEmployee.businessId)
    : branchOptions;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#536DFE',
          color: 'white',
          py: 2.5,
          px: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          Add New Employee
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
          Fill in all the required details to add a new employee
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}
        >
          <Tab label="Personal" icon={<PersonIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
          <Tab label="Company" icon={<BusinessIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
          <Tab label="Bank" icon={<CreditCardIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
          <Tab label="Documents" icon={<DocumentIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
        </Tabs>

        <Box sx={{ p: 3, maxHeight: '60vh', overflowY: 'auto' }}>
          {/* Personal Tab */}
          {activeTab === 0 && (
            <Box>
              <Card sx={{ boxShadow: 2, borderRadius: 2, mb: 2 }}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Personal Information
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  {/* Profile Photo Upload */}
                  <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        mb: 2,
                        border: '4px solid #e0e0e0',
                      }}
                      src={profilePhotoPreview}
                    >
                      <PersonIcon sx={{ fontSize: 60 }} />
                    </Avatar>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="profile-photo-upload"
                      type="file"
                      onChange={handlePhotoChange}
                    />
                    <label htmlFor="profile-photo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        size="small"
                        sx={{ textTransform: 'none' }}
                      >
                        Upload Photo
                      </Button>
                    </label>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Click to add a profile picture
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        size="small"
                        value={newEmployee.password}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, password: e.target.value }))
                        }
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={newEmployee.role}
                          onChange={(e) =>
                            setNewEmployee((prev) => ({ ...prev, role: e.target.value }))
                          }
                          label="Role *"
                        >
                          <MenuItem value="Manager">Manager</MenuItem>
                          <MenuItem value="Trainee">Trainee</MenuItem>
                          <MenuItem value="Employee">Employee</MenuItem>
                          <MenuItem value="HR">HR</MenuItem>
                          <MenuItem value="Finance">Finance</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name "
                        size="small"
                        value={newEmployee.firstName}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, firstName: e.target.value }))
                        }
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name "
                        size="small"
                        value={newEmployee.lastName}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, lastName: e.target.value }))
                        }
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email "
                        type="email"
                        size="small"
                        value={newEmployee.email}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, email: e.target.value }))
                        }
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number "
                        size="small"
                        value={newEmployee.phone}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, phone: e.target.value }))
                        }
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        type="date"
                        size="small"
                        value={newEmployee.dateOfBirth}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, dateOfBirth: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Gender</InputLabel>
                        <Select
                          value={newEmployee.gender}
                          onChange={(e) =>
                            setNewEmployee((prev) => ({ ...prev, gender: e.target.value }))
                          }
                          label="Gender"
                        >
                          <MenuItem value="male">Male</MenuItem>
                          <MenuItem value="female">Female</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        size="small"
                        multiline
                        rows={2}
                        value={newEmployee.address}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, address: e.target.value }))
                        }
                        placeholder="Enter complete address"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Emergency Contact Name"
                        size="small"
                        value={newEmployee.emergencyContact}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, emergencyContact: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Emergency Contact Phone"
                        size="small"
                        value={newEmployee.emergencyPhone}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, emergencyPhone: e.target.value }))
                        }
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Company Tab */}
          {activeTab === 1 && (
            <Box>
              <Card sx={{ boxShadow: 2, borderRadius: 2, mb: 2 }}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessIcon />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Company Information
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Employee Code "
                        size="small"
                        value={newEmployee.employeeCode}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, employeeCode: e.target.value }))
                        }
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Date of Joining "
                        type="date"
                        size="small"
                        value={newEmployee.dateOfJoining}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, dateOfJoining: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Company </InputLabel>
                        <Select
                          value={newEmployee.businessId}
                          onChange={(e) =>
                            setNewEmployee((prev) => ({
                              ...prev,
                              businessId: e.target.value,
                              branchId: '',
                            }))
                          }
                          label="Company "
                        >
                          {businessOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id.toString()}>
                              {opt.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Branch </InputLabel>
                        <Select
                          value={newEmployee.branchId}
                          onChange={(e) =>
                            setNewEmployee((prev) => ({ ...prev, branchId: e.target.value }))
                          }
                          label="Branch "
                        >
                          {filteredBranches.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id.toString()}>
                              {opt.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Department</InputLabel>
                        <Select
                          value={newEmployee.department}
                          onChange={(e) =>
                            setNewEmployee((prev) => ({ ...prev, department: e.target.value }))
                          }
                          label="Department "
                        >
                          {departmentOptions.map((dept) => (
                            <MenuItem key={dept} value={dept}>
                              {dept}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Designation </InputLabel>
                        <Select
                          value={newEmployee.designation}
                          onChange={(e) =>
                            setNewEmployee((prev) => ({ ...prev, designation: e.target.value }))
                          }
                          label="Designation "
                        >
                          {designationOptions.map((designation) => (
                            <MenuItem key={designation} value={designation}>
                              {designation}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Employment Type</InputLabel>
                        <Select
                          value={newEmployee.employmentType}
                          onChange={(e) =>
                            setNewEmployee((prev) => ({ ...prev, employmentType: e.target.value }))
                          }
                          label="Employment Type"
                        >
                          <MenuItem value="Full Time">Full Time</MenuItem>
                          <MenuItem value="Part Time">Part Time</MenuItem>
                          <MenuItem value="Contract">Contract</MenuItem>
                          <MenuItem value="Internship">Internship</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Shift</InputLabel>
                        <Select
                          value={newEmployee.shift}
                          onChange={(e) =>
                            setNewEmployee((prev) => ({ ...prev, shift: e.target.value }))
                          }
                          label="Shift"
                        >
                          <MenuItem value="morning">Morning</MenuItem>
                          <MenuItem value="evening">Evening</MenuItem>
                          <MenuItem value="night">Night</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Work Location</InputLabel>
                        <Select
                          value={newEmployee.workLocation}
                          onChange={(e) =>
                            setNewEmployee((prev) => ({ ...prev, workLocation: e.target.value }))
                          }
                          label="Work Location"
                        >
                          <MenuItem value="office">Office</MenuItem>
                          <MenuItem value="remote">Remote</MenuItem>
                          <MenuItem value="hybrid">Hybrid</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Reporting Manager"
                        size="small"
                        value={newEmployee.reportingManager}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, reportingManager: e.target.value }))
                        }
                        placeholder="Enter reporting manager name"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Bank Tab */}
          {activeTab === 2 && (
            <Box>
              <Card sx={{ boxShadow: 2, borderRadius: 2, mb: 2 }}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <CreditCardIcon />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Bank Information
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Bank Name"
                        size="small"
                        value={newEmployee.bankName}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, bankName: e.target.value }))
                        }
                        placeholder="Enter bank name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account Holder Name"
                        size="small"
                        value={newEmployee.accountHolderName}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, accountHolderName: e.target.value }))
                        }
                        placeholder="Enter account holder name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account Number"
                        size="small"
                        value={newEmployee.accountNumber}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, accountNumber: e.target.value }))
                        }
                        placeholder="Enter account number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="IFSC Code"
                        size="small"
                        value={newEmployee.ifscCode}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, ifscCode: e.target.value }))
                        }
                        placeholder="Enter IFSC code"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="UPI ID"
                        size="small"
                        value={newEmployee.upiId}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, upiId: e.target.value }))
                        }
                        placeholder="Enter UPI ID"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Documents Tab */}
          {activeTab === 3 && (
            <Box>
              <Card sx={{ boxShadow: 2, borderRadius: 2, mb: 2 }}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <DocumentIcon />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Document Upload
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    {['Resume/CV', 'Aadhaar Card', 'PAN Card', 'Passport', 'Educational Certificates', 'Experience Letters'].map((doc) => (
                      <Grid item xs={12} sm={6} key={doc}>
                        <Box
                          sx={{
                            border: '2px dashed #e0e0e0',
                            borderRadius: 2,
                            p: 2,
                            textAlign: 'center',
                            '&:hover': { borderColor: '#536DFE', bgcolor: '#f8f9fa' },
                          }}
                        >
                          <UploadIcon sx={{ fontSize: 40, color: '#999', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary" mb={1}>
                            {doc}
                          </Typography>
                          <input
                            type="file"
                            style={{ display: 'none' }}
                            id={`upload-${doc}`}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          />
                          <label htmlFor={`upload-${doc}`}>
                            <Button
                              variant="outlined"
                              component="span"
                              size="small"
                              sx={{ textTransform: 'none' }}
                            >
                              Choose File
                            </Button>
                          </label>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
        <Button onClick={handleClose} variant="outlined" sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
          }}
        >
          Add Employee
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEmployee;

